import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdmin } from '../AdminContentEditor';
import { 
  ArrowLeft, 
  Play, 
  BookOpen, 
  MessageSquare, 
  Unlock,
  Lock,
  CheckCircle,
  ExternalLink,
  Sparkles,
  Calendar,
  User,
  Laptop,
  Users,
  Rocket,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Bell,
  FileText,
  Shield,
  Lightbulb,
  Target,
  TrendingUp,
  DollarSign,
  Zap,
  Palette,
  Settings,
  BarChart3,
  Package,
  Building2,
  ArrowRight,
  Quote,
  Check,
  X,
  Eye,
  Wrench,
  GraduationCap,
  CircleDot,
  Bot,
  Database,
  Link as LinkIcon,
  PenLine,
  Smartphone,
  QrCode,
  Briefcase,
  Heart,
  Star,
  Coffee,
  Search,
  Share2,
  Layers,
  Code,
  Globe,
  Mail,
  Instagram,
  Clock,
  Copy,
  Armchair
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

// Phygital logo
const PHYGITAL_LOGO = 'https://customer-assets.emergentagent.com/job_brainfood-resources/artifacts/5ba8azs7_1%20copy.png';
const NAOMI_AVATAR = 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg';

// Color palette
const colors = {
  primary: '#000000',
  accent: '#8B5CF6',
  teal: '#00CED1',
  purple: '#8B5CF6',
  indigo: '#6366F1',
  pink: '#EC4899',
  background: '#FAFAFA',
};

// Navigation sections for HQ
const hqSections = [
  { id: 'about', label: 'About', icon: Building2 },
  { id: 'product', label: 'Product', icon: Package },
  { id: 'marketing', label: 'Marketing', icon: TrendingUp },
  { id: 'admin', label: 'Admin', icon: Settings },
  { id: 'team', label: 'Team', icon: Users },
];

// Progress sections
const progressSections = [
  { id: 'journal', label: 'Journal Entry', icon: PenLine, color: '#8B5CF6' },
  { id: 'what-i-did', label: 'What I Did', icon: CheckCircle, color: '#10B981' },
  { id: 'what-i-used', label: 'What I Used', icon: Wrench, color: '#6366F1' },
  { id: 'what-i-learned', label: 'What I Learned', icon: GraduationCap, color: '#F59E0B' },
  { id: 'whats-next', label: "What's Next", icon: Rocket, color: '#EC4899' },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare, color: '#00CED1' },
];

// About Phygital content
const aboutPhygital = {
  tagline: 'Your preferences, perfectly communicated',
  mission: 'Stop explaining yourself at every appointment. Store your preferences once and share with any stylist.',
  problem: 'Every time you visit a new stylist, you have to re-explain everything. Your texture, your sensitivities, your style preferences. It is exhausting and leads to miscommunication.',
  solution: 'Phygital creates a preference layer between consumers and service providers. Save once, share anywhere.',
  howItWorks: [
    { step: 1, title: 'Set Up', desc: 'Your preferences' },
    { step: 2, title: 'Share', desc: 'Send a link or QR' },
    { step: 3, title: 'Done', desc: 'Get what you want' },
  ],
  forConsumers: [
    'Share with anyone via link or QR code',
    'All profiles in one place (hair, nails, makeup, lash)',
    'Preview before sharing',
    'Accessibility and invisible disabilities supported',
  ],
  forProviders: [
    'Client directory with preferences and history',
    'Portfolio showcase from Instagram/TikTok',
    'Collect deposits (Venmo, CashApp, Zelle)',
    'Connect tools (Acuity, Square, StyleSeat)',
  ],
  categories: ['Hair and Styling', 'Nails', 'Makeup', 'Lashes', 'Events', 'Wellness'],
};

// Product features
const productFeatures = {
  consumer: [
    { name: 'Wallet Card', status: 'Live', desc: 'Digital preference card' },
    { name: 'Defaults Management', status: 'Live', desc: 'Save category preferences' },
    { name: 'Inspo Board', status: 'Live', desc: 'Save inspiration photos' },
    { name: 'Experience Planning', status: 'Live', desc: 'Plan appointments by occasion' },
    { name: 'Share Wallet', status: 'Live', desc: 'QR code and link sharing' },
    { name: 'Provider Search', status: 'Testing', desc: 'Find providers' },
  ],
  provider: [
    { name: 'Client Kanban', status: 'Live', desc: 'View client preferences' },
    { name: 'Portfolio', status: 'Live', desc: 'Showcase work' },
    { name: 'Acuity Integration', status: 'Mocked', desc: 'Calendar sync' },
    { name: 'Analytics', status: 'Mocked', desc: 'Business insights' },
  ],
};

// January data
const januaryData = {
  month: 'January 2026',
  title: 'The Spark',
  subtitle: 'From Idea to MVP',
  summary: 'Turned a nail salon visit into a tech startup. Built the MVP, secured 3 pilots, and established the foundation.',
  
  journalEntry: {
    date: 'January 31, 2026',
    title: 'Month 1 Reflections',
    content: `This month was a whirlwind. It started with a simple observation at a nail salon: why do I have to explain my preferences every single time? That frustration became Phygital.

I moved fast. Maybe too fast? But that is the startup game. Built the MVP on Emergent in days, not weeks. Interviewed real users (hair stylists, MUAs) and learned my assumptions were wrong in interesting ways. The hair stylist needed completely different features than I imagined.

The biggest wins: securing 3 pilot providers and getting the core product working. The biggest lesson: interview users before you build. I would have saved time if I had talked to more people earlier.

What surprised me most was how much operational work goes into a startup. It is not just building the product. It is the legal setup, the content strategy, the engagement systems, the waitlist flows. All of it matters.

Going into February, I am focused on infrastructure and automation. The foundation is laid. Now it is time to build on it.`,
  },

  whatIDid: [
    {
      category: 'Ideation and Research',
      items: [
        { title: 'Identified the problem', desc: 'During a nail salon visit, noticed friction in service provider workflows' },
        { title: 'Strategized use cases', desc: 'Hair stylists, MUAs, event coordinators, personal stylists' },
        { title: 'Market research on Perplexity', desc: 'Analyzed competitors, market size, and integration opportunities' },
      ]
    },
    {
      category: 'Building',
      items: [
        { title: 'Built MVP on Emergent', desc: 'Full-stack app from zero to working product in days' },
        { title: 'Brand identity', desc: 'Logo design and color scheme that reflects the phygital concept' },
        { title: 'User profiles and beta accounts', desc: 'Set up testing infrastructure for early users' },
      ]
    },
    {
      category: 'Business Operations',
      items: [
        { title: 'Legal setup', desc: 'Business registration, terms of service, privacy policy' },
        { title: 'Strategy', desc: 'Pricing models, go-to-market plan, competitive positioning' },
        { title: 'Business plan', desc: 'Comprehensive documentation for stakeholders' },
        { title: 'Notion HQ', desc: 'Central hub for all company documentation' },
      ]
    },
    {
      category: 'Pilots and Validation',
      items: [
        { title: 'Secured 3 pilots', desc: 'Hair stylist, MUA, and a fashion x tech event' },
        { title: 'User interviews', desc: 'Hair and MUA interviews revealed different needs than expected' },
      ]
    },
    {
      category: 'Personal Brand',
      items: [
        { title: 'Website on Emergent', desc: 'techbaenae.com for personal brand presence' },
        { title: 'Data Dreaming community', desc: 'Thought leadership platform' },
        { title: 'LinkedIn cleanup', desc: 'Professional presence optimization' },
      ]
    },
    {
      category: 'Systems and Operations',
      items: [
        { title: 'Content operations', desc: 'Workflows for consistent content creation' },
        { title: 'Engagement system', desc: 'Community interaction framework' },
        { title: 'Waitlist and approval system', desc: 'Controlled access for early users' },
        { title: 'Admin dashboard', desc: 'Internal tools for managing the platform' },
        { title: 'Beehiiv email API', desc: 'Newsletter and user communication' },
        { title: 'Instagram DM research', desc: 'Exploring share-to-DM connections' },
      ]
    },
  ],

  whatIUsed: [
    { name: 'Emergent', category: 'Development', desc: 'Built entire MVP', link: 'https://app.emergent.sh/?via=naomi' },
    { name: 'Perplexity', category: 'Research', desc: 'Market analysis and research', link: 'https://perplexity.ai' },
    { name: 'Notion', category: 'Documentation', desc: 'Company HQ and knowledge base', link: 'https://notion.so' },
    { name: 'Beehiiv', category: 'Email', desc: 'Newsletter and waitlist', link: 'https://beehiiv.com' },
    { name: 'Canva', category: 'Design', desc: 'Brand assets and visuals', link: 'https://canva.com' },
    { name: 'Airtable', category: 'Data', desc: 'User management and tracking', link: 'https://airtable.com' },
  ],

  whatILearned: [
    {
      title: 'Interview users ASAP',
      insight: 'Hair stylist had different needs than expected. MUA brought ideas I had not considered. Do not build in a vacuum.',
      icon: Users,
      applyToYours: 'Before building anything, talk to at least 3 potential users. Their feedback will surprise you.',
    },
    {
      title: 'Start small, then build',
      insight: 'Focused on solo user type first. Team features can come later. Scope creep is real.',
      icon: Target,
      applyToYours: 'Pick ONE user type to serve first. Resist the urge to build for everyone.',
    },
    {
      title: 'Document everything',
      insight: 'Solid structure makes organizing easier. Systems are easier to maintain when documented from day one.',
      icon: FileText,
      applyToYours: 'Create a Notion (or similar) from day one. Future you will thank present you.',
    },
  ],

  whatsNext: [
    { title: 'Banking and payments', desc: 'Set up business bank account and Stripe integration', status: 'pending' },
    { title: 'User interviews', desc: 'Continue gathering feedback and improving product', status: 'in-progress' },
    { title: 'Pilot invitations', desc: 'Special invites to beta testers', status: 'pending' },
    { title: 'Content moderation', desc: 'Build tools for managing user-generated content', status: 'pending' },
    { title: 'Acuity API', desc: 'Calendar and booking integration', status: 'research' },
    { title: 'Team strategy', desc: 'Plan for scaling the team', status: 'planning' },
  ],

  feedbackQuestions: [
    'What was helpful for you?',
    'What are you still confused about?',
    'What do you want to know more of?',
  ],
};

// February is locked
const februaryData = {
  month: 'February 2026',
  title: 'Building Foundation',
  subtitle: 'Infrastructure and Automation',
  isLocked: true,
  unlocksAt: 'End of February 2026',
  preview: 'Automation, AI agents, and scaling infrastructure. Submit feedback on January to get notified when this unlocks.',
};

// User Flow Diagram Component
const UserFlowDiagram = ({ title, steps }) => (
  <div className="p-5 rounded-2xl bg-gray-50 border border-gray-200">
    <h4 className="font-semibold text-gray-900 mb-4">{title}</h4>
    <div className="flex items-center gap-2 overflow-x-auto pb-2">
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <div className="flex-shrink-0 w-24 p-3 rounded-xl bg-white border border-gray-200 text-center">
            <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 text-xs font-bold flex items-center justify-center mx-auto mb-2">
              {i + 1}
            </div>
            <p className="text-xs font-medium text-gray-700">{step}</p>
          </div>
          {i < steps.length - 1 && (
            <ArrowRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
          )}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// Browser Mockup Component
const BrowserMockup = ({ title, children, url }) => (
  <div className="rounded-xl overflow-hidden border border-gray-200 shadow-lg bg-white">
    <div className="bg-gray-100 px-4 py-2 flex items-center gap-2 border-b border-gray-200">
      <div className="flex gap-1.5">
        <div className="w-3 h-3 rounded-full bg-red-400" />
        <div className="w-3 h-3 rounded-full bg-yellow-400" />
        <div className="w-3 h-3 rounded-full bg-green-400" />
      </div>
      {url && (
        <div className="flex-1 mx-4">
          <div className="bg-white rounded-md px-3 py-1 text-xs text-gray-500 border border-gray-200">
            {url}
          </div>
        </div>
      )}
      {title && <span className="text-xs font-medium text-gray-600">{title}</span>}
    </div>
    <div className="p-4 bg-gray-50">
      {children}
    </div>
  </div>
);

// Dashboard Mockup Component
const DashboardMockup = ({ sections }) => (
  <div className="space-y-3">
    <div className="flex gap-2 border-b border-gray-200 pb-2">
      {sections.map((s, i) => (
        <div key={i} className={`px-3 py-1 rounded-md text-xs font-medium ${i === 0 ? 'bg-purple-100 text-purple-700' : 'text-gray-500'}`}>
          {s}
        </div>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-3">
      <div className="col-span-2 h-24 rounded-lg bg-gradient-to-r from-purple-100 to-pink-100 p-3">
        <div className="h-2 w-20 bg-purple-300 rounded mb-2" />
        <div className="h-2 w-32 bg-purple-200 rounded mb-2" />
        <div className="h-8 w-full bg-white/50 rounded mt-2" />
      </div>
      <div className="h-24 rounded-lg bg-gray-100 p-3">
        <div className="h-2 w-12 bg-gray-300 rounded mb-2" />
        <div className="h-12 w-full bg-white rounded mt-2" />
      </div>
    </div>
  </div>
);

// Kanban Board Mockup
const KanbanMockup = () => (
  <div className="flex gap-3">
    {['Backlog', 'In Progress', 'Done'].map((col, i) => (
      <div key={col} className="flex-1 rounded-lg bg-gray-100 p-2">
        <div className="text-xs font-semibold text-gray-600 mb-2">{col}</div>
        <div className="space-y-2">
          {[...Array(i === 2 ? 3 : i === 1 ? 2 : 1)].map((_, j) => (
            <div key={j} className="bg-white p-2 rounded shadow-sm">
              <div className="h-2 w-16 bg-gray-200 rounded mb-1" />
              <div className="h-2 w-12 bg-gray-100 rounded" />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

// Architecture Diagram Component
const ArchitectureDiagram = () => (
  <div className="p-4 rounded-xl bg-gradient-to-br from-slate-50 to-slate-100 border border-slate-200">
    <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-4">System Architecture</h4>
    <div className="relative">
      {/* Frontend Layer */}
      <div className="flex justify-center mb-4">
        <div className="px-4 py-2 bg-purple-100 rounded-lg border border-purple-200 text-xs font-medium text-purple-700">
          React Frontend
        </div>
      </div>
      {/* Arrow */}
      <div className="flex justify-center mb-4">
        <div className="w-px h-6 bg-slate-300" />
      </div>
      {/* API Layer */}
      <div className="flex justify-center gap-4 mb-4">
        <div className="px-3 py-1.5 bg-blue-100 rounded border border-blue-200 text-xs font-medium text-blue-700">API Routes</div>
        <div className="px-3 py-1.5 bg-green-100 rounded border border-green-200 text-xs font-medium text-green-700">Auth</div>
        <div className="px-3 py-1.5 bg-amber-100 rounded border border-amber-200 text-xs font-medium text-amber-700">Webhooks</div>
      </div>
      {/* Arrow */}
      <div className="flex justify-center mb-4">
        <div className="w-px h-6 bg-slate-300" />
      </div>
      {/* Database Layer */}
      <div className="flex justify-center gap-4">
        <div className="px-4 py-2 bg-slate-200 rounded-lg border border-slate-300 text-xs font-medium text-slate-700">
          MongoDB
        </div>
        <div className="px-4 py-2 bg-slate-200 rounded-lg border border-slate-300 text-xs font-medium text-slate-700">
          Beehiiv API
        </div>
      </div>
    </div>
  </div>
);

// User Interview Mockup
const InterviewMockup = ({ persona, insights }) => (
  <div className="p-4 rounded-xl bg-gradient-to-br from-teal-50 to-cyan-50 border border-teal-100">
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-full bg-teal-200 flex items-center justify-center">
        <User className="w-5 h-5 text-teal-700" />
      </div>
      <div>
        <div className="font-medium text-gray-900 text-sm">{persona}</div>
        <div className="text-xs text-teal-600">User Interview</div>
      </div>
    </div>
    <div className="space-y-2">
      {insights.map((insight, i) => (
        <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
          <Quote className="w-3 h-3 text-teal-400 flex-shrink-0 mt-0.5" />
          {insight}
        </div>
      ))}
    </div>
  </div>
);

// Timeline Visual
const TimelineMockup = ({ events }) => (
  <div className="relative pl-6 space-y-4">
    <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-purple-400 to-pink-400" />
    {events.map((event, i) => (
      <div key={i} className="relative">
        <div className="absolute -left-4 w-3 h-3 rounded-full bg-purple-500 border-2 border-white shadow" />
        <div className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm">
          <div className="text-xs font-medium text-purple-600">{event.date}</div>
          <div className="text-sm font-medium text-gray-900">{event.title}</div>
        </div>
      </div>
    ))}
  </div>
);

// Stats Card
const StatsCard = ({ label, value, trend, trendUp }) => (
  <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-sm">
    <div className="text-xs text-gray-500 uppercase tracking-wider">{label}</div>
    <div className="text-2xl font-bold text-gray-900 mt-1">{value}</div>
    {trend && (
      <div className={`text-xs font-medium mt-1 ${trendUp ? 'text-green-600' : 'text-gray-500'}`}>
        {trendUp && <TrendingUp className="w-3 h-3 inline mr-1" />}
        {trend}
      </div>
    )}
  </div>
);

// Brand Identity Mockup
const BrandMockup = () => (
  <div className="p-4 rounded-xl bg-gradient-to-br from-gray-900 to-gray-800">
    <div className="flex items-center gap-4 mb-4">
      <img src={PHYGITAL_LOGO} alt="Phygital" className="h-10 object-contain" />
      <div>
        <div className="text-white font-bold">Phygital</div>
        <div className="text-gray-400 text-xs">Brand Identity</div>
      </div>
    </div>
    <div className="grid grid-cols-4 gap-2">
      <div className="aspect-square rounded-lg bg-purple-500" />
      <div className="aspect-square rounded-lg bg-teal-500" />
      <div className="aspect-square rounded-lg bg-pink-500" />
      <div className="aspect-square rounded-lg bg-indigo-500" />
    </div>
    <div className="mt-3 flex gap-2">
      <div className="px-2 py-1 rounded text-xs text-white bg-white/10">Montserrat</div>
      <div className="px-2 py-1 rounded text-xs text-white bg-white/10">Inter</div>
    </div>
  </div>
);

// Notion HQ Mockup
const NotionMockup = () => (
  <BrowserMockup url="notion.so/phygital-hq">
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-amber-600" />
        </div>
        <span className="font-semibold text-gray-900 text-sm">Phygital HQ</span>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {['Roadmap', 'Docs', 'Meeting Notes', 'Strategy', 'Pilots', 'Content'].map(page => (
          <div key={page} className="p-2 rounded bg-white border border-gray-100 text-xs text-gray-600">
            {page}
          </div>
        ))}
      </div>
    </div>
  </BrowserMockup>
);

// Legal Docs Mockup
const LegalDocsMockup = () => (
  <div className="grid grid-cols-3 gap-3">
    {[
      { title: 'Terms of Service', icon: FileText, color: 'blue' },
      { title: 'Privacy Policy', icon: Shield, color: 'green' },
      { title: 'Business Reg', icon: Building2, color: 'purple' },
    ].map(doc => (
      <div key={doc.title} className={`p-3 rounded-xl bg-${doc.color}-50 border border-${doc.color}-100`}>
        <doc.icon className={`w-6 h-6 text-${doc.color}-500 mb-2`} />
        <div className="text-xs font-medium text-gray-700">{doc.title}</div>
        <div className="flex items-center gap-1 mt-1">
          <Check className="w-3 h-3 text-green-500" />
          <span className="text-xs text-green-600">Complete</span>
        </div>
      </div>
    ))}
  </div>
);

// Pilot Card Mockup
const PilotCardMockup = ({ name, type, status }) => (
  <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-sm">
    <div className="flex items-center justify-between mb-2">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
        {name[0]}
      </div>
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
        status === 'Active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
      }`}>
        {status}
      </span>
    </div>
    <div className="font-medium text-gray-900 text-sm">{name}</div>
    <div className="text-xs text-gray-500">{type}</div>
  </div>
);

// Email Flow Mockup
const EmailFlowMockup = () => (
  <div className="p-4 rounded-xl bg-gradient-to-br from-pink-50 to-rose-50 border border-pink-100">
    <div className="flex items-center gap-2 mb-3">
      <Mail className="w-5 h-5 text-pink-500" />
      <span className="font-medium text-gray-900 text-sm">Beehiiv Email Flow</span>
    </div>
    <div className="flex items-center gap-2">
      {['Signup', 'Welcome', 'Onboard', 'Engage'].map((step, i) => (
        <React.Fragment key={step}>
          <div className="px-2 py-1 rounded bg-white border border-pink-200 text-xs font-medium text-pink-700">
            {step}
          </div>
          {i < 3 && <ArrowRight className="w-3 h-3 text-pink-300" />}
        </React.Fragment>
      ))}
    </div>
  </div>
);

// Social Strategy Mockup
const SocialMockup = () => (
  <div className="grid grid-cols-2 gap-3">
    <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white">
      <Instagram className="w-5 h-5 mb-2" />
      <div className="text-sm font-semibold">Instagram</div>
      <div className="text-xs opacity-80">Content strategy</div>
    </div>
    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 text-white">
      <Globe className="w-5 h-5 mb-2" />
      <div className="text-sm font-semibold">Website</div>
      <div className="text-xs opacity-80">techbaenae.com</div>
    </div>
  </div>
);

// Waitlist Dashboard Mockup  
const WaitlistMockup = () => (
  <BrowserMockup title="Admin Dashboard">
    <div className="space-y-3">
      <div className="flex gap-4">
        <StatsCard label="Waitlist" value="47" trend="+12 this week" trendUp />
        <StatsCard label="Approved" value="8" />
        <StatsCard label="Pilots" value="3" />
      </div>
      <div className="bg-white p-3 rounded-lg border border-gray-100">
        <div className="text-xs font-medium text-gray-600 mb-2">Recent Signups</div>
        <div className="space-y-1">
          {['sarah@...', 'mike@...', 'jen@...'].map(email => (
            <div key={email} className="flex items-center justify-between text-xs">
              <span className="text-gray-600">{email}</span>
              <span className="text-green-600">Pending</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </BrowserMockup>
);

// Mockup Card Component
const MockupCard = ({ title, description, visual }) => (
  <div className="p-4 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200">
    <div className="aspect-video rounded-xl bg-white border border-gray-200 mb-3 flex items-center justify-center overflow-hidden">
      {visual}
    </div>
    <h4 className="font-medium text-gray-900 text-sm">{title}</h4>
    <p className="text-xs text-gray-500">{description}</p>
  </div>
);

// Phone Mockup Component
const PhoneMockup = ({ children, title }) => (
  <div className="w-48 mx-auto">
    <div className="bg-gray-900 rounded-3xl p-2 shadow-xl">
      <div className="bg-white rounded-2xl overflow-hidden">
        <div className="h-6 bg-gray-100 flex items-center justify-center">
          <div className="w-16 h-1 bg-gray-300 rounded-full" />
        </div>
        <div className="p-3 min-h-[200px]">
          {children}
        </div>
      </div>
    </div>
    {title && <p className="text-center text-xs text-gray-500 mt-2">{title}</p>}
  </div>
);

// Phygital Connect Modal
const PhygitalConnectModal = ({ isOpen, onClose, onConnect }) => {
  const [step, setStep] = useState('intro'); // intro, connecting, pending, approved, denied
  const [email, setEmail] = useState('');
  const [integrationCode, setIntegrationCode] = useState('');
  const [copied, setCopied] = useState(false);
  const { checkIsAdmin, adminEmails } = useAdmin();
  const API_URL = window.location.origin;

  // Generate a unique integration code
  useEffect(() => {
    if (isOpen && !integrationCode) {
      const code = 'FR-' + Math.random().toString(36).substring(2, 8).toUpperCase();
      setIntegrationCode(code);
    }
  }, [isOpen, integrationCode]);

  const copyCode = () => {
    navigator.clipboard.writeText(integrationCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    localStorage.setItem('frontrow_email', email);
    localStorage.setItem('frontrow_integration_code', integrationCode);
    setStep('pending');
  };

  const checkApproval = async () => {
    // Check if email is in approved list via API
    try {
      const response = await fetch(`${API_URL}/api/admin/check/${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.is_admin) {
        localStorage.setItem('phygital_connected', 'true');
        localStorage.setItem('frontrow_approved', 'true');
        setStep('approved');
        setTimeout(() => {
          onConnect();
          onClose();
        }, 2000);
      } else {
        setStep('pending');
      }
    } catch (error) {
      // Fallback to context check
      if (checkIsAdmin(email)) {
        localStorage.setItem('phygital_connected', 'true');
        localStorage.setItem('frontrow_approved', 'true');
        setStep('approved');
        setTimeout(() => {
          onConnect();
          onClose();
        }, 2000);
      } else {
        setStep('pending');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl max-w-md w-full overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {step === 'intro' && (
            <>
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <img src={PHYGITAL_LOGO} alt="Phygital" className="h-10 object-contain" />
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Connect with Phygital</h2>
                    <p className="text-sm text-gray-500">Connect to access Front Row</p>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <div className="p-4 rounded-xl bg-purple-50 border border-purple-100 mb-6">
                  <h4 className="font-semibold text-purple-800 mb-2">How it works</h4>
                  <ol className="text-sm text-purple-700 space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="font-bold">1.</span>
                      Fill out your audience preferences on Phygital
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">2.</span>
                      Enter your integration code in Phygital settings
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold">3.</span>
                      Wait for approval to access Front Row content
                    </li>
                  </ol>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Your Integration Code</label>
                    <div className="flex gap-2">
                      <div className="flex-1 px-4 py-3 rounded-xl bg-gray-100 font-mono text-gray-800 text-center">
                        {integrationCode}
                      </div>
                      <button
                        type="button"
                        onClick={copyCode}
                        className="px-4 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-500" />}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Copy this code to your Phygital settings dashboard</p>
                  </div>

                  <a 
                    href="https://phygital.app/audience/frontrow"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 rounded-xl bg-black text-white font-medium text-center hover:bg-gray-800 transition-colors"
                  >
                    Open Phygital
                    <ExternalLink className="w-4 h-4 inline ml-2" />
                  </a>

                  <button
                    type="submit"
                    className="w-full py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors"
                  >
                    I have connected, check my status
                  </button>
                </form>
              </div>

              <div className="px-6 pb-6">
                <button
                  onClick={onClose}
                  className="w-full py-2 text-gray-500 text-sm hover:text-gray-700"
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {step === 'pending' && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Waiting for Approval</h2>
              <p className="text-gray-600 mb-6">
                Your request has been submitted. You will be notified once your access is approved.
              </p>
              <div className="p-4 rounded-xl bg-gray-50 mb-6">
                <p className="text-sm text-gray-600">Integration Code:</p>
                <p className="font-mono text-lg font-bold text-gray-900">{integrationCode}</p>
              </div>
              <button
                onClick={checkApproval}
                className="w-full py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors mb-3"
              >
                Check Approval Status
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 text-gray-500 text-sm hover:text-gray-700"
              >
                Close
              </button>
            </div>
          )}

          {step === 'approved' && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Granted!</h2>
              <p className="text-gray-600">
                Welcome to Front Row. You now have access to exclusive content.
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// QR Code Display Component
const QRCodeDisplay = () => (
  <div className="p-6 rounded-2xl bg-white border border-gray-200 shadow-lg inline-block">
    <div className="w-48 h-48 bg-gray-100 rounded-xl flex items-center justify-center mb-4">
      <QrCode className="w-32 h-32 text-gray-800" />
    </div>
    <p className="text-center text-sm text-gray-600">Scan to share access</p>
  </div>
);

// Waitlist Modal
const WaitlistModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    localStorage.setItem('frontrow_waitlist_email', email);
    setSubmitted(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl max-w-md w-full p-8"
          onClick={e => e.stopPropagation()}
        >
          {!submitted ? (
            <>
              <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center mx-auto mb-6">
                <Bell className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                Get Notified
              </h2>
              <p className="text-gray-600 text-center mb-6">
                Front Row is currently invite-only. Join the waitlist to get notified when more labs become available.
              </p>
              <form onSubmit={handleSubmit}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  type="submit"
                  className="w-full py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors"
                >
                  Join Waitlist
                </button>
              </form>
              <button
                onClick={onClose}
                className="w-full mt-3 py-2 text-gray-500 text-sm hover:text-gray-700"
              >
                Maybe later
              </button>
            </>
          ) : (
            <>
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
                You are on the list!
              </h2>
              <p className="text-gray-600 text-center mb-6">
                We will notify you when new labs become available.
              </p>
              <button
                onClick={onClose}
                className="w-full py-3 rounded-xl bg-gray-900 text-white font-medium hover:bg-gray-800 transition-colors"
              >
                Got it
              </button>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// NDA Modal
const NDAModal = ({ isOpen, onClose, onAccept }) => {
  const [agreed, setAgreed] = useState(false);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl max-w-lg w-full overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Before You Enter</h2>
                <p className="text-sm text-gray-500">Quick agreement required</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                This is a Real Company
              </h4>
              <p className="text-amber-700 text-sm">
                Phygital is a live startup. Information shared here is confidential and pre-mature strategy that should never be shared with others.
              </p>
            </div>

            <p className="text-sm text-gray-600">
              This is not a blueprint to copy. It is a real-time guide to learn from. Take what applies to your company and leave what does not.
            </p>
          </div>

          <div className="p-6 border-t border-gray-100 bg-gray-50">
            <label className="flex items-start gap-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500 mt-0.5"
              />
              <span className="text-sm text-gray-700">
                I understand this is confidential and will not share it.
              </span>
            </label>
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-100">
                Cancel
              </button>
              <button
                onClick={onAccept}
                disabled={!agreed}
                className="flex-1 px-4 py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Enter Front Row
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Progress Section Component
const ProgressSection = ({ section, data, isExpanded, onToggle }) => {
  const Icon = section.icon;
  
  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: `${section.color}15` }}
          >
            <Icon className="w-5 h-5" style={{ color: section.color }} />
          </div>
          <span className="font-semibold text-gray-900">{section.label}</span>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
      </button>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-100"
          >
            <div className="p-5">
              {data}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Tool Card
const ToolCard = ({ tool }) => (
  <a
    href={tool.link}
    target="_blank"
    rel="noopener noreferrer"
    className="flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all group"
  >
    <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center font-bold text-gray-700 text-lg">
      {tool.name[0]}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2">
        <h4 className="font-medium text-gray-900">{tool.name}</h4>
        {tool.discount && (
          <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
            {tool.discount}
          </span>
        )}
      </div>
      <p className="text-sm text-gray-500">{tool.desc}</p>
    </div>
    <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-purple-600" />
  </a>
);

// Learning Card
const LearningCard = ({ item }) => {
  const Icon = item.icon;
  return (
    <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-100">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-amber-600" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-gray-900 mb-2">{item.title}</h4>
          <p className="text-sm text-gray-600 mb-3">{item.insight}</p>
          {item.applyToYours && (
            <div className="p-3 rounded-lg bg-white/60 border border-amber-200">
              <p className="text-xs font-medium text-amber-800">
                <Lightbulb className="w-3 h-3 inline mr-1" />
                Apply to yours: {item.applyToYours}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Next Step Card
const NextStepCard = ({ item }) => {
  const statusColors = {
    'pending': { bg: 'bg-gray-100', text: 'text-gray-600' },
    'in-progress': { bg: 'bg-blue-100', text: 'text-blue-600' },
    'research': { bg: 'bg-purple-100', text: 'text-purple-600' },
    'planning': { bg: 'bg-amber-100', text: 'text-amber-600' },
  };
  const color = statusColors[item.status] || statusColors['pending'];
  
  return (
    <div className="flex items-center gap-4 p-4 rounded-xl border border-gray-200">
      <div className="w-2 h-2 rounded-full bg-pink-500" />
      <div className="flex-1">
        <h4 className="font-medium text-gray-900">{item.title}</h4>
        <p className="text-sm text-gray-500">{item.desc}</p>
      </div>
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${color.bg} ${color.text}`}>
        {item.status}
      </span>
    </div>
  );
};

// HQ Content Components
const HQAbout = () => (
  <div className="space-y-6">
    <div className="p-6 rounded-2xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100">
      <h3 className="text-2xl font-bold text-gray-900 mb-2">{aboutPhygital.tagline}</h3>
      <p className="text-gray-600">{aboutPhygital.mission}</p>
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="p-5 rounded-xl bg-red-50 border border-red-100">
        <h4 className="font-semibold text-red-800 mb-2">The Problem</h4>
        <p className="text-sm text-red-700">{aboutPhygital.problem}</p>
      </div>
      <div className="p-5 rounded-xl bg-green-50 border border-green-100">
        <h4 className="font-semibold text-green-800 mb-2">The Solution</h4>
        <p className="text-sm text-green-700">{aboutPhygital.solution}</p>
      </div>
    </div>

    <UserFlowDiagram 
      title="How It Works" 
      steps={aboutPhygital.howItWorks.map(s => s.title)} 
    />

    <div className="grid md:grid-cols-2 gap-4">
      <div className="p-5 rounded-xl border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-purple-600" />
          For Consumers
        </h4>
        <ul className="space-y-2">
          {aboutPhygital.forConsumers.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </div>
      <div className="p-5 rounded-xl border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-teal-600" />
          For Providers
        </h4>
        <ul className="space-y-2">
          {aboutPhygital.forProviders.map((item, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
              <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

const HQProduct = () => (
  <div className="space-y-6">
    <div className="grid md:grid-cols-3 gap-4">
      <MockupCard 
        title="Consumer Wallet"
        description="Digital preference card"
        visual={
          <PhoneMockup>
            <div className="space-y-2">
              <div className="w-full h-16 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500" />
              <div className="h-2 bg-gray-200 rounded w-3/4" />
              <div className="h-2 bg-gray-100 rounded w-1/2" />
            </div>
          </PhoneMockup>
        }
      />
      <MockupCard 
        title="Share via QR"
        description="One tap sharing"
        visual={
          <div className="p-4">
            <QrCode className="w-20 h-20 mx-auto text-gray-400" />
          </div>
        }
      />
      <MockupCard 
        title="Provider View"
        description="See client preferences"
        visual={
          <PhoneMockup>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gray-200" />
                <div className="h-2 bg-gray-200 rounded flex-1" />
              </div>
              <div className="h-12 bg-purple-100 rounded-lg" />
              <div className="h-12 bg-pink-100 rounded-lg" />
            </div>
          </PhoneMockup>
        }
      />
    </div>

    <div className="grid md:grid-cols-2 gap-4">
      <div className="p-5 rounded-xl border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Consumer Features</h4>
        <div className="space-y-2">
          {productFeatures.consumer.map((f, i) => (
            <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
              <div>
                <span className="font-medium text-gray-900 text-sm">{f.name}</span>
                <p className="text-xs text-gray-500">{f.desc}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                f.status === 'Live' ? 'bg-green-100 text-green-700' :
                f.status === 'Testing' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {f.status}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="p-5 rounded-xl border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-4">Provider Features</h4>
        <div className="space-y-2">
          {productFeatures.provider.map((f, i) => (
            <div key={i} className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50">
              <div>
                <span className="font-medium text-gray-900 text-sm">{f.name}</span>
                <p className="text-xs text-gray-500">{f.desc}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                f.status === 'Live' ? 'bg-green-100 text-green-700' :
                f.status === 'Testing' ? 'bg-blue-100 text-blue-700' :
                'bg-amber-100 text-amber-700'
              }`}>
                {f.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const HQMarketing = () => (
  <div className="space-y-6">
    <div className="p-5 rounded-xl border border-gray-200">
      <h4 className="font-semibold text-gray-900 mb-4">Go-to-Market Strategy</h4>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-purple-50">
          <Instagram className="w-6 h-6 text-purple-600 mb-2" />
          <h5 className="font-medium text-gray-900">Social</h5>
          <p className="text-xs text-gray-600">Content creator partnerships, Instagram and TikTok presence</p>
        </div>
        <div className="p-4 rounded-lg bg-teal-50">
          <Users className="w-6 h-6 text-teal-600 mb-2" />
          <h5 className="font-medium text-gray-900">Events</h5>
          <p className="text-xs text-gray-600">HTHF event pilot, beauty industry trade shows</p>
        </div>
        <div className="p-4 rounded-lg bg-pink-50">
          <Mail className="w-6 h-6 text-pink-600 mb-2" />
          <h5 className="font-medium text-gray-900">Email</h5>
          <p className="text-xs text-gray-600">Beehiiv newsletter, waitlist nurture sequences</p>
        </div>
      </div>
    </div>
    <div className="p-5 rounded-xl bg-gray-50 border border-gray-200">
      <p className="text-sm text-gray-600 text-center">
        Marketing strategy details coming in future months
      </p>
    </div>
  </div>
);

const HQAdmin = () => (
  <div className="space-y-6">
    <div className="p-5 rounded-xl border border-gray-200">
      <h4 className="font-semibold text-gray-900 mb-4">Admin Dashboard Tabs</h4>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
        {['Overview', 'Product', 'Marketing', 'Team', 'Front Row', 'Pilots', 'Users', 'Revenue', 'Settings'].map((tab, i) => (
          <div key={i} className="p-2 rounded-lg bg-gray-50 text-center">
            <span className="text-xs font-medium text-gray-700">{tab}</span>
          </div>
        ))}
      </div>
    </div>
    <div className="p-5 rounded-xl bg-gray-50 border border-gray-200">
      <p className="text-sm text-gray-600 text-center">
        Admin system details coming in future months
      </p>
    </div>
  </div>
);

const HQTeam = () => (
  <div className="space-y-6">
    <div className="p-6 rounded-xl border border-gray-200">
      <div className="flex items-start gap-4">
        <img src={NAOMI_AVATAR} alt="Naomi" className="w-20 h-20 rounded-xl object-cover" />
        <div>
          <h4 className="font-bold text-gray-900">Naomi Thomas</h4>
          <p className="text-purple-600 text-sm mb-2">Founder, Head of Phygital</p>
          <p className="text-sm text-gray-600">
            AI Strategist and Data Dreamer. Building Phygital to solve the preference communication problem in service industries.
          </p>
        </div>
      </div>
    </div>
    <div className="p-5 rounded-xl bg-gray-50 border border-gray-200 text-center">
      <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
      <p className="text-sm text-gray-600">Team expansion plans coming in future months</p>
    </div>
  </div>
);

// Month Content Component
const MonthContent = ({ data }) => {
  const [expandedSection, setExpandedSection] = useState('journal');

  const renderJournal = () => (
    <div className="space-y-4">
      <div className="p-6 rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-100">
        <div className="flex items-center gap-2 text-sm text-purple-600 mb-3">
          <Calendar className="w-4 h-4" />
          {data.journalEntry.date}
        </div>
        <h4 className="font-bold text-gray-900 mb-4">{data.journalEntry.title}</h4>
        <div className="prose prose-sm max-w-none">
          {data.journalEntry.content.split('\n\n').map((p, i) => (
            <p key={i} className="text-gray-600 mb-3">{p}</p>
          ))}
        </div>
      </div>
    </div>
  );

  const renderWhatIDid = () => (
    <div className="space-y-8">
      {/* Ideation and Research */}
      <div>
        <h4 className="font-medium text-gray-500 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          Ideation and Research
        </h4>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            {data.whatIDid[0].items.map((item, j) => (
              <div key={j} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">{item.title}</h5>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <InterviewMockup 
              persona="Hair Stylist" 
              insights={[
                "Needs client history at a glance",
                "Wants to see past services quickly",
                "Portfolio is crucial for new clients"
              ]}
            />
            <InterviewMockup 
              persona="Makeup Artist" 
              insights={[
                "Event type changes everything",
                "Skin sensitivities are critical",
                "Reference photos save time"
              ]}
            />
          </div>
        </div>
        <UserFlowDiagram 
          title="Research Process" 
          steps={['Identify Problem', 'Define Users', 'Market Research', 'Competitor Analysis', 'Validate Idea']}
        />
      </div>

      {/* Building */}
      <div>
        <h4 className="font-medium text-gray-500 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Code className="w-4 h-4 text-blue-500" />
          Building
        </h4>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            {data.whatIDid[1].items.map((item, j) => (
              <div key={j} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">{item.title}</h5>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <ArchitectureDiagram />
            <BrandMockup />
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <BrowserMockup url="phygital.app" title="Consumer App">
            <div className="space-y-2">
              <div className="h-16 rounded-lg bg-gradient-to-r from-purple-400 to-pink-400" />
              <div className="h-2 w-20 bg-gray-200 rounded" />
              <div className="h-2 w-32 bg-gray-100 rounded" />
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="h-12 bg-purple-50 rounded" />
                <div className="h-12 bg-pink-50 rounded" />
              </div>
            </div>
          </BrowserMockup>
          <BrowserMockup url="phygital.app/provider" title="Provider Dashboard">
            <DashboardMockup sections={['Clients', 'Calendar', 'Portfolio', 'Settings']} />
          </BrowserMockup>
          <BrowserMockup url="phygital.app/admin" title="Admin Panel">
            <KanbanMockup />
          </BrowserMockup>
        </div>
      </div>

      {/* Business Operations */}
      <div>
        <h4 className="font-medium text-gray-500 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-purple-500" />
          Business Operations
        </h4>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            {data.whatIDid[2].items.map((item, j) => (
              <div key={j} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">{item.title}</h5>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <LegalDocsMockup />
            <NotionMockup />
          </div>
        </div>
        <TimelineMockup events={[
          { date: 'Week 1', title: 'Business Registration Completed' },
          { date: 'Week 2', title: 'Terms of Service Drafted' },
          { date: 'Week 3', title: 'Privacy Policy Published' },
          { date: 'Week 4', title: 'Notion HQ Organized' },
        ]} />
      </div>

      {/* Pilots and Validation */}
      <div>
        <h4 className="font-medium text-gray-500 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Rocket className="w-4 h-4 text-pink-500" />
          Pilots and Validation
        </h4>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            {data.whatIDid[3].items.map((item, j) => (
              <div key={j} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">{item.title}</h5>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-3">
            <PilotCardMockup name="Maya" type="Hair Stylist" status="Active" />
            <PilotCardMockup name="Jordan" type="Makeup Artist" status="Active" />
            <PilotCardMockup name="HTHF Event" type="Fashion x Tech" status="Scheduled" />
          </div>
        </div>
        <UserFlowDiagram 
          title="Pilot Onboarding Flow" 
          steps={['Identify Pilot', 'Initial Call', 'Setup Account', 'Training', 'Go Live', 'Feedback Loop']}
        />
      </div>

      {/* Personal Brand */}
      <div>
        <h4 className="font-medium text-gray-500 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Star className="w-4 h-4 text-amber-500" />
          Personal Brand
        </h4>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            {data.whatIDid[4].items.map((item, j) => (
              <div key={j} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">{item.title}</h5>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <SocialMockup />
            <BrowserMockup url="techbaenae.com" title="Personal Brand Site">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-purple-200" />
                  <div className="h-2 w-24 bg-gray-200 rounded" />
                </div>
                <div className="h-12 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg" />
                <div className="grid grid-cols-2 gap-2">
                  <div className="h-8 bg-gray-100 rounded" />
                  <div className="h-8 bg-gray-100 rounded" />
                </div>
              </div>
            </BrowserMockup>
          </div>
        </div>
      </div>

      {/* Systems and Operations */}
      <div>
        <h4 className="font-medium text-gray-500 text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
          <Settings className="w-4 h-4 text-indigo-500" />
          Systems and Operations
        </h4>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div className="space-y-3">
            {data.whatIDid[5].items.map((item, j) => (
              <div key={j} className="flex items-start gap-3 p-3 rounded-xl bg-gray-50">
                <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h5 className="font-medium text-gray-900">{item.title}</h5>
                  <p className="text-sm text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="space-y-4">
            <EmailFlowMockup />
            <WaitlistMockup />
          </div>
        </div>
        <div className="p-4 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100">
          <h5 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
            <Layers className="w-4 h-4 text-indigo-500" />
            Systems Overview
          </h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { name: 'Content Ops', status: 'Active', icon: FileText },
              { name: 'Engagement', status: 'Active', icon: Heart },
              { name: 'Waitlist', status: 'Active', icon: Users },
              { name: 'Admin', status: 'Active', icon: Settings },
            ].map(sys => (
              <div key={sys.name} className="p-3 rounded-lg bg-white border border-gray-200">
                <sys.icon className="w-5 h-5 text-indigo-500 mb-2" />
                <div className="text-sm font-medium text-gray-900">{sys.name}</div>
                <div className="text-xs text-green-600 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  {sys.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const renderWhatIUsed = () => (
    <div className="grid md:grid-cols-2 gap-3">
      {data.whatIUsed.map((tool, i) => (
        <ToolCard key={i} tool={tool} />
      ))}
    </div>
  );

  const renderWhatILearned = () => (
    <div className="space-y-4">
      {data.whatILearned.map((item, i) => (
        <LearningCard key={i} item={item} />
      ))}
    </div>
  );

  const renderWhatsNext = () => (
    <div className="space-y-3">
      {data.whatsNext.map((item, i) => (
        <NextStepCard key={i} item={item} />
      ))}
    </div>
  );

  const renderFeedback = () => (
    <div className="space-y-4">
      <p className="text-gray-600 mb-4">Please share your thoughts:</p>
      <ul className="space-y-3">
        {data.feedbackQuestions.map((q, i) => (
          <li key={i} className="flex items-center gap-3 text-gray-700">
            <CircleDot className="w-4 h-4 text-purple-500" />
            {q}
          </li>
        ))}
      </ul>
      <textarea
        placeholder="Type your feedback here..."
        className="w-full p-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[120px] mt-4"
      />
      <button className="w-full py-3 rounded-xl bg-teal-500 text-white font-medium hover:bg-teal-600 transition-colors">
        Submit Feedback
      </button>
    </div>
  );

  const sectionContent = {
    'journal': renderJournal(),
    'what-i-did': renderWhatIDid(),
    'what-i-used': renderWhatIUsed(),
    'what-i-learned': renderWhatILearned(),
    'whats-next': renderWhatsNext(),
    'feedback': renderFeedback(),
  };

  return (
    <div className="space-y-4">
      {progressSections.map((section) => (
        <ProgressSection
          key={section.id}
          section={section}
          data={sectionContent[section.id]}
          isExpanded={expandedSection === section.id}
          onToggle={() => setExpandedSection(expandedSection === section.id ? null : section.id)}
        />
      ))}
    </div>
  );
};

// Access Gate for Front Row - requires Data Dreaming invite approval
const FrontRowAccessGate = ({ onRequestAccess }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [checking, setChecking] = useState(false);
  const { checkIsAdmin } = useAdmin();
  const API_URL = window.location.origin;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setChecking(true);
    
    // Check if email is in approved list via API
    try {
      const response = await fetch(`${API_URL}/api/admin/check/${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (data.is_admin) {
        localStorage.setItem('frontrow_approved', 'true');
        localStorage.setItem('frontrow_email', email);
        onRequestAccess();
      } else {
        setSubmitted(true);
        localStorage.setItem('frontrow_waitlist_email', email);
      }
    } catch (error) {
      // Fallback to context check
      if (checkIsAdmin(email)) {
        localStorage.setItem('frontrow_approved', 'true');
        localStorage.setItem('frontrow_email', email);
        onRequestAccess();
      } else {
        setSubmitted(true);
        localStorage.setItem('frontrow_waitlist_email', email);
      }
    }
    setChecking(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex items-center justify-center px-4">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="w-full max-w-md text-center"
      >
        {/* Seat Icon */}
        <div className="w-24 h-24 rounded-2xl bg-black flex items-center justify-center mx-auto mb-6">
          <Armchair className="w-16 h-16 text-white" />
        </div>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Front Row</h1>
        <p className="text-gray-500 mb-8">
          A behind-the-scenes look at building a tech startup
        </p>

        {!submitted ? (
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-6 h-6 text-purple-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Required</h2>
            <p className="text-gray-600 text-sm mb-6">
              Front Row is invite-only. Enter your email to check if you have been approved through the Data Dreaming invite.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-purple-500 text-center"
              />
              <button
                type="submit"
                disabled={checking}
                className="w-full py-3 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors disabled:opacity-50"
              >
                {checking ? 'Checking...' : 'Check Access'}
              </button>
            </form>

            <p className="text-xs text-gray-400 mt-4">
              Need access? Fill out the Data Dreaming invite first.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
            <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-gray-900 mb-2">You Do Not Have Access</h2>
            <p className="text-gray-600 text-sm mb-6">
              Your email is not on the approved list. Please fill out the Data Dreaming invite and wait for approval.
            </p>

            <a 
              href="https://datadreaming.cloud"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors"
            >
              Go to Data Dreaming
            </a>

            <p className="text-xs text-gray-400 mt-4">
              You will be notified when your access is approved.
            </p>
          </div>
        )}

        <Link to="/" className="inline-flex items-center gap-2 text-gray-500 text-sm mt-8 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" />
          Back to Data Dreaming
        </Link>
      </motion.div>
    </div>
  );
};

const FrontRowPage = () => {
  const navigate = useNavigate();
  const { isAdmin, currentUserEmail } = useAdmin();
  const [isLoading, setIsLoading] = useState(true);
  const [isPhygitalConnected, setIsPhygitalConnected] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [activeHQSection, setActiveHQSection] = useState('about');
  const [showProgress, setShowProgress] = useState(false);

  useEffect(() => {
    // Wait a moment for auth to settle
    const timer = setTimeout(() => {
      // Direct access for specific email
      const userEmail = localStorage.getItem('soiree_email') || localStorage.getItem('frontrow_email') || currentUserEmail;
      if (userEmail && userEmail.toLowerCase() === 'me@iamnaomithomas.com') {
        setIsLoading(false);
        return;
      }
      
      // Check if user is approved or admin - if not, redirect to home
      const approved = localStorage.getItem('frontrow_approved');
      const soireeApproved = localStorage.getItem('soiree_approved');
      
      // Allow access if: frontrow_approved, soiree_approved (admin), isAdmin, or has currentUserEmail (authenticated)
      if (!approved && !soireeApproved && !isAdmin && !currentUserEmail) {
        navigate('/');
        return;
      }
      
      const connected = localStorage.getItem('phygital_connected');
      if (connected) setIsPhygitalConnected(true);
      setIsLoading(false);
    }, 500); // Small delay to allow auth to settle
    
    return () => clearTimeout(timer);
  }, [navigate, isAdmin, currentUserEmail]);

  const handleConnect = () => {
    setIsPhygitalConnected(true);
    localStorage.setItem('phygital_connected', 'true');
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hqContent = {
    about: <HQAbout />,
    product: <HQProduct />,
    marketing: <HQMarketing />,
    admin: <HQAdmin />,
    team: <HQTeam />,
  };

  // Show loading state while checking access
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-black flex items-center justify-center mx-auto mb-4">
            <Armchair className="w-10 h-10 text-white" />
          </div>
          <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  // Main page (only shown to approved users)
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100 sticky top-0 bg-white z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                <Armchair className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-gray-900">Front Row</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Powered by</span>
            <img src={PHYGITAL_LOGO} alt="Phygital" className="h-12 object-contain" />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-8">
            <div className="w-14 h-14 rounded-2xl bg-black flex items-center justify-center">
              <Armchair className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900">Front Row</h1>
          </div>

          <p className="text-sm font-medium text-purple-600 uppercase tracking-wider mb-4">
            For Students and Builders
          </p>
          
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Get a Front Row Seat to<br />Building a Tech Startup
          </h2>
          
          <p className="text-xl text-gray-600 mb-4 max-w-2xl mx-auto">
            Watch someone launch a startup from the ground up.
          </p>
          
          <p className="text-gray-500 mb-8 max-w-xl mx-auto">
            Not a blueprint to copy and paste, but a real-time guide to learn from. Take what applies to your company.
          </p>

          {!isPhygitalConnected ? (
            <div className="space-y-4">
              <Link
                to="/lab/frontrow/month-1"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-colors"
                data-testid="month1-btn"
              >
                <Calendar className="w-6 h-6" />
                Month 1
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-gray-400 text-sm">
                Start with the first workshop
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-2 text-green-600 mb-4">
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">Connected to Phygital</span>
              </div>

              <Link
                to="/lab/frontrow/month-1"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 transition-colors"
                data-testid="view-month1-btn"
              >
                <Calendar className="w-5 h-5" />
                View Month 1 Workshop
                <ArrowRight className="w-5 h-5" />
              </Link>
              <p className="text-gray-400 text-sm">
                Workshop content
              </p>
            </div>
          )}
        </div>
      </section>

      {/* About Front Row */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">About Front Row</h3>
          <p className="text-gray-600 mb-8">
            A month-by-month look at building Phygital from idea to launch. Real decisions, real mistakes, real lessons.
          </p>

          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[
              { icon: Eye, title: 'Watch', desc: 'Monthly progress updates' },
              { icon: BookOpen, title: 'Learn', desc: 'Tools, resources, lessons' },
              { icon: MessageSquare, title: 'Reflect', desc: 'Submit your feedback' },
              { icon: Unlock, title: 'Unlock', desc: 'Access next month' },
            ].map((item) => (
              <div key={item.title} className="p-5 rounded-2xl bg-white border border-gray-200">
                <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center mb-3">
                  <item.icon className="w-5 h-5 text-purple-600" />
                </div>
                <h4 className="font-semibold text-gray-900">{item.title}</h4>
                <p className="text-sm text-gray-500">{item.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Meet Your Guide */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Meet Your Guide</h3>

          <div className="flex flex-col md:flex-row gap-8 items-start">
            <img src={NAOMI_AVATAR} alt="Naomi Thomas" className="w-32 h-32 rounded-2xl object-cover" />
            <div>
              <h4 className="text-xl font-bold text-gray-900">Naomi Thomas</h4>
              <p className="text-purple-600 mb-3">AI Strategist and Data Dreamer</p>
              <div className="flex flex-wrap gap-4 mb-4">
                <a 
                  href="https://linkedin.com/in/techbaenae" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  @techbaenae
                </a>
                <a 
                  href="https://www.techbaenae.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <Globe className="w-4 h-4" />
                  techbaenae.com
                </a>
                <a 
                  href="mailto:naomi@techbaenae.com"
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                >
                  <Mail className="w-4 h-4" />
                  naomi@techbaenae.com
                </a>
              </div>
              <p className="text-gray-600">
                Helping builders, innovators, and data dreamers harness the power of AI 
                and data to grow their businesses while keeping their authentic voice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-gray-100">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">Powered by</span>
            <img src={PHYGITAL_LOGO} alt="Phygital" className="h-15 object-contain" style={{ height: '60px' }} />
          </div>
          <Link to="/" className="text-sm text-gray-500 hover:text-gray-700">Home</Link>
        </div>
      </footer>

      {/* Progress Modal */}
      <AnimatePresence>
        {showProgress && hasSignedNDA && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto"
          >
            <div className="min-h-screen py-8 px-4">
              <div className="max-w-4xl mx-auto bg-white rounded-3xl overflow-hidden shadow-2xl">
                {/* Modal Header */}
                <div className="sticky top-0 bg-white border-b border-gray-100 p-4 flex items-center justify-between z-10">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center">
                      <span className="text-white font-bold text-sm">FR</span>
                    </div>
                    <span className="font-semibold text-gray-900">Building Phygital</span>
                  </div>
                  <button 
                    onClick={() => setShowProgress(false)}
                    className="p-2 rounded-lg hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                <div className="p-6">
                  {/* Phygital HQ Section */}
                  <div className="mb-8">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Phygital HQ</h2>
                    <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
                      {hqSections.map((section) => {
                        const Icon = section.icon;
                        return (
                          <button
                            key={section.id}
                            onClick={() => setActiveHQSection(section.id)}
                            className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-all ${
                              activeHQSection === section.id
                                ? 'bg-black text-white'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            {section.label}
                          </button>
                        );
                      })}
                    </div>
                    {hqContent[activeHQSection]}
                  </div>

                  {/* Month Selector */}
                  <div className="mb-6">
                    <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">Progress</h2>
                    <div className="flex gap-3">
                      <div className="flex-1 p-4 rounded-2xl border-2 border-purple-500 bg-purple-50">
                        <span className="text-xs font-medium text-purple-600 uppercase">{januaryData.month}</span>
                        <h3 className="font-semibold text-gray-900">{januaryData.title}</h3>
                        <p className="text-sm text-gray-500">{januaryData.subtitle}</p>
                      </div>
                      <div className="flex-1 p-4 rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 opacity-60">
                        <div className="flex items-center gap-2 text-gray-400 mb-1">
                          <Lock className="w-4 h-4" />
                          <span className="text-xs font-medium uppercase">{februaryData.month}</span>
                        </div>
                        <h3 className="font-semibold text-gray-400">{februaryData.title}</h3>
                        <p className="text-xs text-gray-400">Unlocks {februaryData.unlocksAt}</p>
                      </div>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="mb-6 p-4 rounded-xl bg-gray-50 border border-gray-200">
                    <p className="text-gray-600">{januaryData.summary}</p>
                  </div>

                  {/* Month Content */}
                  <MonthContent data={januaryData} />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <WaitlistModal isOpen={showWaitlistModal} onClose={() => setShowWaitlistModal(false)} />
      <PhygitalConnectModal 
        isOpen={showConnectModal} 
        onClose={() => setShowConnectModal(false)} 
        onConnect={handleConnect}
      />
    </div>
  );
};

export default FrontRowPage;
